<template>
    <!-- Dialog -->
    <q-dialog v-model="showDialog" @hide="close">
        <q-card class="select-widget-card">
            <!-- Dialog header -->
            <q-bar class="bg-primary">
                <div class="text-white">
                    {{ dialogTitle }}
                </div>

                <q-space />

                <q-btn
                    @click="close"
                    class="text-white"
                    icon="close"
                    dense
                    flat
                >
                    <q-tooltip>{{ $t('Close') }}</q-tooltip>
                </q-btn>
            </q-bar>

            <!-- Widget type card selection -->
            <q-card-section class="row" v-if="!selectedWidgetType">
                <div class="col-xs-12 col-sm-6 q-mb-md">
                    <q-card
                        class="flex items-center justify-center no-shadow cursor-pointer full-height"
                        @click="selectedWidgetType = 'Event'"
                        :class="$q.screen.lt.sm ? '' : 'q-mr-sm'"
                        bordered
                    >
                        <div class="row q-my-md">
                            <div
                                class="col-sm-3 col-xs-12 flex items-center justify-center"
                            >
                                <q-icon
                                    name="fa-solid fa-calendar-days"
                                    size="lg"
                                    color="grey"
                                    :class="{ 'q-mb-sm': $q.screen.lt.sm }"
                                />
                            </div>
                            <div
                                class="col q-px-sm"
                                :class="{ 'text-center': $q.screen.lt.sm }"
                            >
                                <div class="text-body1 text-bold">
                                    {{ $t('Event') }}
                                </div>
                                <div class="q-pt-sm">
                                    {{
                                        $t(
                                            'Monitor uptime and track downtime events, including duration and count.'
                                        )
                                    }}
                                </div>
                            </div>
                        </div>
                    </q-card>
                </div>
                <div class="col-xs-12 col-sm-6 q-mb-md">
                    <q-card
                        class="flex items-center justify-center no-shadow cursor-pointer full-height"
                        @click="selectedWidgetType = 'KPI'"
                        :class="$q.screen.lt.sm ? '' : 'q-ml-sm'"
                        bordered
                    >
                        <div class="row q-my-md">
                            <div
                                class="col-sm-3 col-xs-12 flex items-center justify-center"
                            >
                                <q-icon
                                    name="fa-solid fa-file-chart-column"
                                    size="lg"
                                    color="grey"
                                    :class="{ 'q-mb-sm': $q.screen.lt.sm }"
                                />
                            </div>
                            <div
                                class="col q-px-sm"
                                :class="{ 'text-center': $q.screen.lt.sm }"
                            >
                                <div class="text-body1 text-bold">
                                    {{ $t('KPI') }}
                                </div>
                                <div class="q-pt-sm">
                                    {{
                                        $t(
                                            'Track key performance indicators like quality, efficiency, and OEE.'
                                        )
                                    }}
                                </div>
                            </div>
                        </div>
                    </q-card>
                </div>
                <div
                    class="col-xs-12 col-sm-6"
                    :class="{ 'q-mb-md': $q.screen.lt.sm }"
                >
                    <q-card
                        class="flex items-center justify-center no-shadow cursor-pointer full-height"
                        @click="selectedWidgetType = 'Production'"
                        :class="$q.screen.lt.sm ? '' : 'q-mr-sm'"
                        bordered
                    >
                        <div class="row q-my-md">
                            <div
                                class="col-sm-3 col-xs-12 flex items-center justify-center"
                            >
                                <q-icon
                                    name="fa-solid fa-conveyor-belt-arm"
                                    size="lg"
                                    color="grey"
                                    :class="{ 'q-mb-sm': $q.screen.lt.sm }"
                                />
                            </div>
                            <div
                                class="col q-px-sm"
                                :class="{ 'text-center': $q.screen.lt.sm }"
                            >
                                <div class="text-body1 text-bold">
                                    {{ $t('Production') }}
                                </div>
                                <div class="q-pt-sm">
                                    {{
                                        $t(
                                            'Display production counts and quantities in real-time.'
                                        )
                                    }}
                                </div>
                            </div>
                        </div>
                    </q-card>
                </div>
                <div class="col-xs-12 col-sm-6">
                    <q-card
                        class="flex items-center justify-center no-shadow cursor-pointer full-height"
                        @click="selectedWidgetType = 'Speed'"
                        :class="$q.screen.lt.sm ? '' : 'q-ml-sm'"
                        bordered
                    >
                        <div class="row q-my-md">
                            <div
                                class="col-sm-3 col-xs-12 flex items-center justify-center"
                            >
                                <q-icon
                                    name="fa-solid fa-gauge-max"
                                    size="lg"
                                    color="grey"
                                    :class="{ 'q-mb-sm': $q.screen.lt.sm }"
                                />
                            </div>
                            <div
                                class="col q-px-sm"
                                :class="{ 'text-center': $q.screen.lt.sm }"
                            >
                                <div class="text-body1 text-bold">
                                    {{ $t('Speed') }}
                                </div>
                                <div class="q-pt-sm">
                                    {{
                                        $t(
                                            'Measure the current production line speed.'
                                        )
                                    }}
                                </div>
                            </div>
                        </div>
                    </q-card>
                </div>
            </q-card-section>

            <!-- Widget description -->
            <q-card-section v-if="selectedWidgetType">
                <div class="text-secondary">
                    {{ $t('Widget Description') }}
                </div>
                <div>
                    {{ $t(widgetDescription) }}
                </div>
            </q-card-section>

            <!-- KPI -->
            <q-card-section v-if="selectedWidgetType === 'KPI'">
                <div class="q-gutter-md">
                    <div class="text-secondary">
                        {{ $t('What metric would you like to track?') }}

                        <q-select
                            v-model="kpi.selectedMetric"
                            :options="kpi.metricOptions"
                            color="accent"
                            class="full-width"
                            outlined
                            dense
                        />
                    </div>
                    <div class="text-secondary">
                        {{ $t('What is the period to be considered?') }}
                        <q-select
                            v-model="kpi.selectedPeriod"
                            :options="kpi.periodOptions"
                            color="accent"
                            class="full-width"
                            outlined
                            dense
                        />
                    </div>
                </div>
            </q-card-section>

            <!-- Production -->
            <q-card-section v-if="selectedWidgetType === 'Production'">
                <div class="q-gutter-md">
                    <div class="text-secondary">
                        {{ $t('What type of sensor would you like to track?') }}
                        <q-select
                            :model-value="production.selectedCountType"
                            @update:model-value="
                                val => {
                                    production.selectedCountType = val;
                                    production.selectedUnit = null;
                                    production.selectedPeriod = null;
                                    production.selectedShiftOption = null;
                                }
                            "
                            :options="production.countTypeOptions"
                            color="accent"
                            class="full-width"
                            outlined
                            dense
                        />
                    </div>
                    <div class="text-secondary">
                        {{ $t('What unit would you like to use?') }}
                        <q-select
                            :model-value="production.selectedUnit"
                            @update:model-value="
                                val => {
                                    production.selectedUnit = val;
                                    production.selectedPeriod = null;
                                    production.selectedShiftOption = null;
                                }
                            "
                            :options="productionUnitOptions"
                            color="accent"
                            class="full-width"
                            outlined
                            dense
                        />
                    </div>
                    <div
                        v-if="productionPeriodOptions.length"
                        class="text-secondary"
                    >
                        {{ $t('What is the period to be considered?') }}
                        <q-select
                            :model-value="production.selectedPeriod"
                            @update:model-value="
                                val => {
                                    production.selectedPeriod = val;
                                    production.selectedShiftOption = null;
                                }
                            "
                            :options="productionPeriodOptions"
                            color="accent"
                            class="full-width"
                            outlined
                            dense
                        />
                    </div>

                    <div
                        class="text-secondary"
                        v-if="
                            productionShiftOptions.length &&
                            production.selectedPeriod &&
                            production.selectedPeriod.value === 'Shift'
                        "
                    >
                        <div class="q-gutter-sm">
                            <q-radio
                                v-for="option in productionShiftOptions"
                                :key="option.label"
                                v-model="production.selectedShiftOption"
                                :val="option.value"
                                :label="option.label"
                            />
                        </div>
                    </div>

                    <div
                        class="text-secondary"
                        v-if="
                            production.selectedUnit &&
                            production.selectedPeriod &&
                            production.selectedUnit.value === 'Quantity' &&
                            production.selectedPeriod.value === 'Last 60 min'
                        "
                    >
                        <div class="q-gutter-sm">
                            <q-radio
                                v-for="option in productionQuantityUnitOptions"
                                :key="option.label"
                                v-model="production.selectedQuantityUnit"
                                :val="option.value"
                                :label="option.label"
                            />
                        </div>
                    </div>
                </div>
            </q-card-section>

            <!-- Speed -->
            <q-card-section v-if="selectedWidgetType === 'Speed'">
                <div class="q-gutter-md">
                    <div class="text-secondary">
                        {{ $t('Select how to measure the speed.') }}
                        <q-select
                            v-model="speed.selectedUnit"
                            :options="speed.unitOptions"
                            color="accent"
                            class="full-width"
                            outlined
                            dense
                        />
                    </div>
                    <div class="text-secondary">
                        {{ $t('Select a time unit for measurement.') }}
                        <q-select
                            v-model="speed.selectedTimeUnit"
                            :options="speed.timeUnitOptions"
                            color="accent"
                            class="full-width"
                            outlined
                            dense
                        />
                    </div>
                    <div class="text-secondary">
                        {{ $t('What is the period to be considered?') }}
                        <q-select
                            v-model="speed.selectedPeriod"
                            :options="speed.periodOptions"
                            color="accent"
                            class="full-width"
                            outlined
                            dense
                        />
                    </div>
                </div>
            </q-card-section>

            <!-- Event -->
            <q-card-section v-if="selectedWidgetType === 'Event'">
                <div class="q-gutter-md">
                    <div class="text-secondary">
                        {{ $t('What metric would you like to track?') }}
                        <q-select
                            :model-value="event.selectedMetric"
                            @update:model-value="
                                val => {
                                    event.selectedMetric = val;
                                    event.selectedPeriod = null;
                                }
                            "
                            :options="event.metricOptions"
                            color="accent"
                            class="full-width"
                            outlined
                            dense
                        />
                    </div>

                    <div
                        class="text-secondary"
                        v-if="eventsPeriodOptions.length"
                    >
                        {{ $t('What is the period to be considered?') }}
                        <q-select
                            v-model="event.selectedPeriod"
                            :options="eventsPeriodOptions"
                            color="accent"
                            class="full-width"
                            outlined
                            dense
                        />
                    </div>
                </div>
            </q-card-section>

            <!-- Dialog actions -->
            <q-card-section class="row justify-between">
                <div>
                    <q-btn
                        v-if="selectedWidget && selectedWidgetType"
                        @click="addWidgetWhileEditing"
                        class="q-mr-md"
                        color="white"
                        text-color="primary"
                        :disable="!hasSelectedWidgetChanged"
                        outline
                    >
                        {{ $t('Add new widget') }}
                    </q-btn>
                </div>

                <q-btn
                    v-if="!selectedWidgetType"
                    @click="close"
                    color="white"
                    text-color="primary"
                    outline
                >
                    {{ $t('Cancel') }}
                </q-btn>
                <div v-if="selectedWidgetType">
                    <q-btn
                        @click="
                            {
                                selectedWidgetType = null;
                                resetSelectedOptions();
                            }
                        "
                        color="white"
                        text-color="primary"
                        outline
                    >
                        {{ $t('Back') }}
                    </q-btn>
                    <q-btn
                        @click="selectWidget"
                        color="primary"
                        class="q-ml-md"
                        :disable="!isSelectionValid"
                    >
                        {{ selectedWidget ? $t('Modify Widget') : $t('Add') }}
                    </q-btn>
                </div>
            </q-card-section>
        </q-card>
    </q-dialog>
</template>

<script>
import { WIDGET_TYPE_TO_PROPS } from '@models/widgets.js';
import { mapGetters } from 'vuex';
import { mapState } from 'pinia';
import { useProductsStore } from '@stores/products.js';

export default {
    name: 'SelectWidgetDialog',
    emits: ['close', 'add-widget', 'add-widget-while-editing'],
    props: {
        selectedWidget: {
            type: String,
            default: null
        },
        facilityId: {
            type: Number,
            required: true
        },
        assetId: {
            type: Number,
            required: true
        },
        productId: {
            type: Number,
            required: true
        },
        unitMeasure: {
            type: String,
            required: true
        }
    },
    created() {
        for (const key of Object.keys(WIDGET_TYPE_TO_PROPS)) {
            const filterOptions = WIDGET_TYPE_TO_PROPS[key].filterOptions;

            // skip if filterOptions is not defined
            if (!filterOptions) {
                continue;
            }

            // Initialize all available widgets
            this.allAvailableWidgets.push({
                name: key,
                filterOptions: filterOptions
            });

            // Initialize KPI options
            if (filterOptions.type === 'KPI') {
                this.initializeKpiOptions(filterOptions, key);
            }

            // Initialize production options
            if (filterOptions.type === 'Production') {
                this.initializeProductionOptions(filterOptions, key);
            }

            // Initialize speed options
            if (filterOptions.type === 'Speed') {
                this.initializeSpeedOptions(filterOptions, key);
            }

            // Initialize event options
            if (filterOptions.type === 'Event') {
                this.initializeEventsOptions(filterOptions, key);
            }
        }
    },
    mounted() {
        if (!this.selectedWidget) {
            this.resetSelectedOptions();
        }

        // update the selectedQuantityUnit according to the unitMeasure
        this.updateSelectedQuantityUnit();
    },
    watch: {
        /**
         * Automatically select the first metric option if there is only one.
         * If this.production.selectedCountType.value is set to 'Reject', and
         * this.production.selectedUnit.value is set to 'Quantity',
         * there will only be one period option available.
         * @param options
         */
        productionPeriodOptions(options) {
            if (options.length === 1) {
                this.production.selectedPeriod = options[0];
            }
        },
        /**
         * Update the selected quantity unit based on the unit measure.
         * @param value
         */
        unitMeasure() {
            this.updateSelectedQuantityUnit();
        }
    },
    data() {
        return {
            showDialog: true,
            allAvailableWidgets: [],
            selectedWidgetType: null,
            // KPI
            kpi: {
                metricOptions: [],
                selectedMetric: null,
                periodOptions: [],
                selectedPeriod: null,
                addedMetrics: new Set(),
                addedPeriods: new Set()
            },

            // Production
            production: {
                selectedCountType: null,
                countTypeOptions: [],
                selectedUnit: null,
                unitOptions: [],
                selectedPeriod: null,
                periodOptions: [],
                selectedShiftOption: null,
                shiftOptions: [],
                quantityUnitOptions: [],
                selectedQuantityUnit: null,
                addedCountTypes: new Set(),
                addedUnits: new Set(),
                addedPeriods: new Set(),
                addedShiftOptions: new Set(),
                addedQuantityUnits: new Set()
            },

            // Speed
            speed: {
                selectedUnit: null,
                unitOptions: [],
                selectedTimeUnit: null,
                timeUnitOptions: [],
                selectedPeriod: null,
                periodOptions: [],
                addedUnits: new Set(),
                addedTimeUnits: new Set(),
                addedPeriods: new Set()
            },

            // Event
            event: {
                selectedMetric: null,
                metricOptions: [],
                selectedPeriod: null,
                periodOptions: [],
                addedMetric: new Set(),
                addedPeriods: new Set()
            },

            // Selected widget options to be used when editing
            selectedWidgetOptions: {}
        };
    },
    computed: {
        ...mapGetters({
            getFacility: 'facilities/getFacility'
        }),
        ...mapState(useProductsStore, ['getProductLink']),
        /**
         * Get the widget not found message.
         * @returns {String} - Widget not found message.
         */
        widgetNotFoundMessage() {
            return this.$t(
                'Widget not found. Please select a different widget.'
            );
        },
        /**
         * Select KPI widget.
         * @returns {string} - KPI widget name.
         */
        selectKpiWidget() {
            if (!this.kpi.selectedMetric || !this.kpi.selectedPeriod) {
                return null;
            }

            const kpiWidget = this.allAvailableWidgets.find(
                widget =>
                    widget.filterOptions.type === 'KPI' &&
                    widget.filterOptions.metric ===
                        this.kpi.selectedMetric.value &&
                    widget.filterOptions.period ===
                        this.kpi.selectedPeriod.value
            );
            return kpiWidget ? kpiWidget.name : null;
        },
        /**
         * Select production widget.
         * @returns {string} - Production widget name.
         */
        selectProductionWidget() {
            if (
                !this.production.selectedCountType ||
                !this.production.selectedUnit
            ) {
                return null;
            }

            // Filter production widgets based on selected count type and unit
            const productionWidgets = this.allAvailableWidgets.filter(
                widget =>
                    widget.filterOptions.type === 'Production' &&
                    widget.filterOptions.countType ===
                        this.production.selectedCountType.value &&
                    (widget.filterOptions.unit ===
                        this.production.selectedUnit.value ||
                        widget.filterOptions.altUnit ===
                            this.production.selectedUnit.value)
            );

            /**
             * If there are no period options, it means that the current
             * selectedCountType and selectedUnit only have one widget
             */
            if (!this.productionPeriodOptions.length) {
                return productionWidgets[0] ? productionWidgets[0].name : null;
            }

            /**
             * If there are period options, find the widget that matches the
             * corresponding selected period
             */
            if (
                this.production.selectedPeriod &&
                !this.productionShiftOptions.length &&
                !this.productionQuantityUnitOptions.length
            ) {
                const productionWidget = productionWidgets.find(
                    widget =>
                        widget.filterOptions.period ===
                        this.production.selectedPeriod.value
                );

                return productionWidget ? productionWidget.name : null;
            }

            if (
                this.productionShiftOptions.length &&
                this.production.selectedShiftOption
            ) {
                const productionWidget = productionWidgets.find(
                    widget =>
                        widget.filterOptions.period ===
                            this.production.selectedPeriod.value &&
                        widget.filterOptions.shiftOption ===
                            this.production.selectedShiftOption
                );

                return productionWidget ? productionWidget.name : null;
            }

            if (
                this.productionQuantityUnitOptions.length &&
                this.production.selectedQuantityUnit
            ) {
                const productionWidget = productionWidgets.find(
                    widget =>
                        widget.filterOptions.period ===
                            this.production.selectedPeriod.value &&
                        widget.filterOptions.quantityUnit ===
                            this.production.selectedQuantityUnit
                );

                return productionWidget ? productionWidget.name : null;
            }

            return null;
        },
        /**
         * Select speed widget.
         * @returns {string} - Speed widget name.
         */
        selectSpeedWidget() {
            if (
                !this.speed.selectedUnit ||
                !this.speed.selectedTimeUnit ||
                !this.speed.selectedPeriod
            ) {
                return null;
            }

            const speedWidget = this.allAvailableWidgets.find(
                widget =>
                    widget.filterOptions.type === 'Speed' &&
                    (widget.filterOptions.unit ===
                        this.speed.selectedUnit.value ||
                        widget.filterOptions.altUnit ===
                            this.speed.selectedUnit.value) &&
                    widget.filterOptions.timeUnit ===
                        this.speed.selectedTimeUnit.value &&
                    widget.filterOptions.period ===
                        this.speed.selectedPeriod.value
            );
            return speedWidget ? speedWidget.name : null;
        },
        /**
         * Select event widget.
         * @returns {string} - Event widget name.
         */
        selectEventWidget() {
            if (
                !this.event.selectedMetric ||
                (this.eventsPeriodOptions.length && !this.event.selectedPeriod)
            ) {
                return null;
            }

            const eventWidgets = this.allAvailableWidgets.find(
                widget =>
                    widget.filterOptions.type === 'Event' &&
                    widget.filterOptions.metric ===
                        this.event.selectedMetric.value &&
                    (!this.event.selectedPeriod ||
                        widget.filterOptions.period ===
                            this.event.selectedPeriod.value)
            );

            return eventWidgets ? eventWidgets.name : null;
        },
        /**
         * Filter event period options.
         * @returns {Array} - Filtered event period options.
         */
        eventsPeriodOptions() {
            const options = [];

            if (
                !this.event.selectedMetric ||
                this.event.selectedMetric.value === 'Current phase duration' ||
                this.event.selectedMetric.value === 'Current downtime duration'
            ) {
                return options;
            }

            for (const key of Object.keys(WIDGET_TYPE_TO_PROPS)) {
                const filterOptions = WIDGET_TYPE_TO_PROPS[key].filterOptions;

                // skip if filterOptions is not defined
                if (!filterOptions || !filterOptions.period) {
                    continue;
                }

                // Add event options
                if (
                    filterOptions.type === 'Event' &&
                    this.event.selectedMetric
                ) {
                    if (!this.event.addedPeriods.has(filterOptions.period)) {
                        this.event.addedPeriods.add(filterOptions.period);
                        options.push({
                            label: this.$t(filterOptions.period),
                            value: filterOptions.period
                        });
                    }
                }
            }
            // clear added period
            this.event.addedPeriods.clear();

            this.sortArrayByValue(options);

            return options;
        },
        /**
         * Filter production unit options.
         * @returns {Array} - Filtered production unit options.
         */
        productionUnitOptions() {
            const options = [];

            for (const key of Object.keys(WIDGET_TYPE_TO_PROPS)) {
                const filterOptions = WIDGET_TYPE_TO_PROPS[key].filterOptions;

                // skip if filterOptions is not defined
                if (!filterOptions) {
                    continue;
                }

                // Add production options
                if (
                    filterOptions.type === 'Production' &&
                    this.production.selectedCountType &&
                    filterOptions.countType ===
                        this.production.selectedCountType.value
                ) {
                    let unit = filterOptions.unit;

                    // if package factor is less than 1, use alternative unit
                    if (
                        filterOptions.altUnit &&
                        (this.getProductPackageFactor.production < 1 ||
                            this.getProductPackageFactor.net < 1 ||
                            this.getProductPackageFactor.reject < 1)
                    ) {
                        unit = filterOptions.altUnit;
                    }

                    if (!this.production.addedUnits.has(unit)) {
                        this.production.addedUnits.add(unit);
                        options.push({
                            label: this.$t(unit),
                            value: unit
                        });
                    }
                }
            }
            // clear added unit
            this.production.addedUnits.clear();

            this.sortArrayByValue(options);

            return options;
        },
        /**
         * Filter production period options.
         * @returns {Array} - Filtered production period options.
         */
        productionPeriodOptions() {
            const options = [];

            if (
                !this.production.selectedCountType ||
                !this.production.selectedUnit
            ) {
                return options;
            }

            for (const key of Object.keys(WIDGET_TYPE_TO_PROPS)) {
                const filterOptions = WIDGET_TYPE_TO_PROPS[key].filterOptions;

                // skip if filterOptions or period is not defined
                if (!filterOptions || !filterOptions.period) {
                    continue;
                }

                // Add production options
                if (
                    filterOptions.type === 'Production' &&
                    this.production.selectedCountType &&
                    filterOptions.countType ===
                        this.production.selectedCountType.value &&
                    this.production.selectedUnit &&
                    filterOptions.unit === this.production.selectedUnit.value
                ) {
                    if (
                        !this.production.addedPeriods.has(filterOptions.period)
                    ) {
                        this.production.addedPeriods.add(filterOptions.period);
                        options.push({
                            label: this.$t(filterOptions.period),
                            value: filterOptions.period
                        });
                    }
                }
            }
            // clear added period
            this.production.addedPeriods.clear();

            this.sortArrayByValue(options);

            return options;
        },
        /**
         * Filter production shift options.
         * @returns {Array} - Filtered production shift options.
         */
        productionShiftOptions() {
            const options = [];

            if (
                !this.production.selectedCountType ||
                !this.production.selectedUnit ||
                !this.production.selectedPeriod
            ) {
                return options;
            }

            for (const key of Object.keys(WIDGET_TYPE_TO_PROPS)) {
                const filterOptions = WIDGET_TYPE_TO_PROPS[key].filterOptions;

                // skip if filterOptions or shiftOption is not defined
                if (!filterOptions || !filterOptions.shiftOption) {
                    continue;
                }

                // Add production options
                if (
                    filterOptions.type === 'Production' &&
                    filterOptions.countType ===
                        this.production.selectedCountType.value &&
                    filterOptions.unit === this.production.selectedUnit.value &&
                    filterOptions.period ===
                        this.production.selectedPeriod.value
                ) {
                    if (
                        filterOptions.shiftOption &&
                        !this.production.addedShiftOptions.has(
                            filterOptions.shiftOption
                        )
                    ) {
                        this.production.addedShiftOptions.add(
                            filterOptions.shiftOption
                        );
                        options.push({
                            label: this.$t(filterOptions.shiftOption),
                            value: filterOptions.shiftOption
                        });
                    }
                }
            }
            // clear added shift option
            this.production.addedShiftOptions.clear();

            this.sortArrayByValue(options);

            return options;
        },
        /**
         * Filter production quantity unit options.
         * @returns {Array} - Filtered production quantity unit options.
         */
        productionQuantityUnitOptions() {
            const options = [];

            if (
                !this.production.selectedCountType ||
                !this.production.selectedUnit ||
                !this.production.selectedPeriod
            ) {
                return options;
            }

            for (const key of Object.keys(WIDGET_TYPE_TO_PROPS)) {
                const filterOptions = WIDGET_TYPE_TO_PROPS[key].filterOptions;

                // skip if filterOptions is not defined
                if (!filterOptions) {
                    continue;
                }

                // Add production options
                if (
                    filterOptions.type === 'Production' &&
                    filterOptions.countType ===
                        this.production.selectedCountType.value &&
                    filterOptions.unit === this.production.selectedUnit.value &&
                    filterOptions.period ===
                        this.production.selectedPeriod.value
                ) {
                    if (
                        filterOptions.quantityUnit &&
                        !this.production.addedQuantityUnits.has(
                            filterOptions.quantityUnit
                        )
                    ) {
                        this.production.addedQuantityUnits.add(
                            filterOptions.quantityUnit
                        );
                        options.push({
                            label: this.$t(filterOptions.quantityUnit),
                            value: filterOptions.quantityUnit
                        });
                    }
                }
            }
            // clear added quantity unit
            this.production.addedQuantityUnits.clear();

            this.sortArrayByValue(options);

            return options;
        },
        /**
         * Check if the selection is valid.
         * @returns {Boolean} - True if the selection is valid.
         */
        isSelectionValid() {
            if (!this.selectedWidgetType) {
                return false;
            }

            if (this.selectedWidgetType === 'Event') {
                return this.selectEventWidget ? true : false;
            }

            if (this.selectedWidgetType === 'KPI') {
                return this.selectKpiWidget ? true : false;
            }

            if (this.selectedWidgetType === 'Production') {
                return this.selectProductionWidget ? true : false;
            }

            if (this.selectedWidgetType === 'Speed') {
                return this.selectSpeedWidget ? true : false;
            }

            return false;
        },
        /**
         * Check if there are changes in the selected fields.
         * @returns {Boolean} - True if there are changes.
         */
        hasSelectedWidgetChanged() {
            if (this.selectedWidgetType === 'Event' && this.selectEventWidget) {
                return this.selectedWidget !== this.selectEventWidget;
            }

            if (this.selectedWidgetType === 'KPI' && this.selectKpiWidget) {
                return this.selectedWidget !== this.selectKpiWidget;
            }

            if (
                this.selectedWidgetType === 'Production' &&
                this.selectProductionWidget
            ) {
                return this.selectedWidget !== this.selectProductionWidget;
            }

            if (this.selectedWidgetType === 'Speed' && this.selectSpeedWidget) {
                return this.selectedWidget !== this.selectSpeedWidget;
            }

            return false;
        },
        /**
         * Get the dialog title.
         * @returns {String} - Dialog title.
         */
        dialogTitle() {
            if (this.selectedWidgetType) {
                switch (this.selectedWidgetType) {
                    case 'Event':
                        return this.$t(
                            'Select an event widget to monitor downtimes'
                        );
                    case 'KPI':
                        return this.$t(
                            'Choose a KPI widget to track performance'
                        );
                    case 'Production':
                        return this.$t(
                            'Select a production widget for real-time counts'
                        );
                    case 'Speed':
                        return this.$t(
                            'Choose a speed widget to monitor line performance'
                        );
                }
            }

            return this.$t('Choose a widget type to add to the dashboard');
        },
        /**
         * Get the widget description.
         * @returns {String} - Widget description.
         */
        widgetDescription() {
            if (this.selectedWidgetType === 'Event') {
                if (this.selectEventWidget) {
                    return WIDGET_TYPE_TO_PROPS[this.selectEventWidget]
                        .description;
                }
            }

            if (this.selectedWidgetType === 'KPI') {
                if (this.selectKpiWidget) {
                    return WIDGET_TYPE_TO_PROPS[this.selectKpiWidget]
                        .description;
                }
            }

            if (this.selectedWidgetType === 'Production') {
                if (this.selectProductionWidget) {
                    const widgetType =
                        WIDGET_TYPE_TO_PROPS[this.selectProductionWidget];

                    // if package factor is less than 1,
                    // use alternative description
                    if (
                        widgetType['altDescription'] &&
                        (this.getProductPackageFactor.production < 1 ||
                            this.getProductPackageFactor.net < 1 ||
                            this.getProductPackageFactor.reject < 1)
                    ) {
                        return widgetType['altDescription'];
                    }

                    return widgetType.description;
                }
            }

            if (this.selectedWidgetType === 'Speed') {
                if (this.selectSpeedWidget) {
                    const widgetType =
                        WIDGET_TYPE_TO_PROPS[this.selectSpeedWidget];
                    // if production package factor is less than 1,
                    // use alternative description
                    if (
                        widgetType['altDescription'] &&
                        this.getProductPackageFactor.production < 1
                    ) {
                        return widgetType['altDescription'];
                    }

                    return widgetType.description;
                }
            }

            return this.$t('Please select a widget to see its details.');
        },
        /**
         * Get the package factor for the product.
         * @return {Object} - Packaging factor for the product.
         */
        getProductPackageFactor() {
            const productLink = this.getProductLink({
                assetId: this.assetId,
                productId: this.productId
            });
            const packageFactor = productLink?.packaging_factors;
            if (!packageFactor || typeof packageFactor !== 'object') {
                return {
                    production: 1.0,
                    net: 1.0,
                    reject: 1.0
                };
            }
            return productLink.packaging_factors;
        }
    },
    methods: {
        /**
         * Initialize KPI options.
         * @param {Object} filterOptions - Filter options for the widget.
         * @param {String} key - Widget key.
         */
        initializeKpiOptions(filterOptions, key) {
            const metric = filterOptions.metric;
            const period = filterOptions.period;
            const type = filterOptions.type;
            if (!this.kpi.addedMetrics.has(metric)) {
                this.kpi.addedMetrics.add(metric);
                this.kpi.metricOptions.push({
                    label: this.$t(metric),
                    value: metric
                });
            }
            if (!this.kpi.addedPeriods.has(period)) {
                this.kpi.addedPeriods.add(period);
                this.kpi.periodOptions.push({
                    label: this.$t(period),
                    value: period
                });
            }

            this.sortArrayByValue(this.kpi.metricOptions);
            this.sortArrayByValue(this.kpi.periodOptions);

            // if editing widget, set selected values
            if (this.selectedWidget === key) {
                this.selectedWidgetType = type;
                this.kpi.selectedMetric = {
                    label: this.$t(metric),
                    value: metric
                };
                this.kpi.selectedPeriod = {
                    label: this.$t(period),
                    value: period
                };

                // set selected widget options
                this.selectedWidgetOptions = {
                    type: type,
                    metric: metric,
                    period: period
                };
            }
        },
        /**
         * Initialize production options.
         * @param {Object} filterOptions - Filter options for the widget.
         * @param {String} key - Widget key.
         */
        initializeProductionOptions(filterOptions, key) {
            const countType = filterOptions.countType;
            const shiftOption = filterOptions.shiftOption;
            const quantityUnit = filterOptions.quantityUnit;
            const type = filterOptions.type;
            const period = filterOptions.period;
            let unit = filterOptions.unit;

            if (!this.production.addedCountTypes.has(countType)) {
                this.production.addedCountTypes.add(countType);
                this.production.countTypeOptions.push({
                    label: this.$t(countType),
                    value: countType
                });
            }

            this.sortArrayByValue(this.production.countTypeOptions);

            // if editing widget, set selected values
            if (this.selectedWidget === key) {
                // if package factor is less than 1, use alternative unit
                if (
                    filterOptions.altUnit &&
                    (this.getProductPackageFactor.production < 1 ||
                        this.getProductPackageFactor.net < 1 ||
                        this.getProductPackageFactor.reject < 1)
                ) {
                    unit = filterOptions.altUnit;
                }

                this.selectedWidgetType = type;
                this.production.selectedCountType = {
                    label: this.$t(countType),
                    value: countType
                };
                this.production.selectedUnit = {
                    label: this.$t(unit),
                    value: unit
                };

                if (period) {
                    this.production.selectedPeriod = {
                        label: this.$t(period),
                        value: period
                    };
                }

                if (shiftOption) {
                    this.production.selectedShiftOption = shiftOption;
                }
                if (quantityUnit) {
                    this.production.selectedQuantityUnit = quantityUnit;
                }

                // set selected widget options
                this.selectedWidgetOptions = {
                    type: type,
                    countType: countType,
                    unit: unit,
                    period: period,
                    shiftOption: shiftOption,
                    quantityUnit: quantityUnit
                };
            }
        },
        /**
         * Initialize speed options.
         * @param {Object} filterOptions - Filter options for the widget.
         * @param {String} key - Widget key.
         */
        initializeSpeedOptions(filterOptions, key) {
            let unit = filterOptions.unit;
            const timeUnit = filterOptions.timeUnit;
            const period = filterOptions.period;
            const type = filterOptions.type;

            // if production package factor is less than 1, use alternative unit
            if (
                filterOptions.altUnit &&
                this.getProductPackageFactor.production < 1
            ) {
                unit = filterOptions.altUnit;
            }

            if (!this.speed.addedUnits.has(unit)) {
                this.speed.addedUnits.add(unit);
                this.speed.unitOptions.push({
                    label: this.$t(unit),
                    value: unit
                });
            }

            if (!this.speed.addedTimeUnits.has(timeUnit)) {
                this.speed.addedTimeUnits.add(timeUnit);
                this.speed.timeUnitOptions.push({
                    label: this.$t(timeUnit),
                    value: timeUnit
                });
            }

            if (!this.speed.addedPeriods.has(period)) {
                this.speed.addedPeriods.add(period);
                this.speed.periodOptions.push({
                    label: this.$t(period),
                    value: period
                });
            }

            this.sortArrayByValue(this.speed.unitOptions);
            this.sortArrayByValue(this.speed.timeUnitOptions);
            this.sortArrayByValue(this.speed.periodOptions);

            // if editing widget, set selected values
            if (this.selectedWidget === key) {
                this.selectedWidgetType = type;
                this.speed.selectedUnit = {
                    label: this.$t(unit),
                    value: unit
                };
                this.speed.selectedTimeUnit = {
                    label: this.$t(timeUnit),
                    value: timeUnit
                };
                this.speed.selectedPeriod = {
                    label: this.$t(period),
                    value: period
                };

                // set selected widget options
                this.selectedWidgetOptions = {
                    type: type,
                    unit: unit,
                    timeUnit: timeUnit,
                    period: period
                };
            }
        },
        /**
         * Initialize event options.
         * @param {Object} filterOptions - Filter options for the widget.
         * @param {String} key - Widget key.
         */
        initializeEventsOptions(filterOptions, key) {
            const metric = filterOptions.metric;
            const period = filterOptions.period;
            const type = filterOptions.type;

            if (!this.event.addedMetric.has(metric)) {
                this.event.addedMetric.add(metric);
                this.event.metricOptions.push({
                    label: this.$t(metric),
                    value: metric
                });
            }

            this.sortArrayByValue(this.event.metricOptions);

            // if editing widget, set selected values
            if (this.selectedWidget === key) {
                this.selectedWidgetType = type;
                this.event.selectedMetric = {
                    label: this.$t(metric),
                    value: metric
                };

                if (period) {
                    this.event.selectedPeriod = {
                        label: this.$t(period),
                        value: period
                    };
                }

                // set selected widget options
                this.selectedWidgetOptions = {
                    type: type,
                    metric: metric,
                    period: period
                };
            }
        },
        /**
         * Emit close.
         */
        close() {
            this.resetSelectedOptions();
            this.$emit('close');
        },
        /**
         * Add a new widget while editing.
         * Emit add-widget-while-editing event.
         */
        addWidgetWhileEditing() {
            if (!this.selectedWidgetType) {
                return;
            }

            // add KPI widget
            if (this.selectedWidgetType === 'KPI') {
                if (this.selectKpiWidget === null) {
                    this.$notifyError(this.widgetNotFoundMessage);
                    return;
                }
                this.$emit('add-widget-while-editing', this.selectKpiWidget);
            }

            // add production widget
            if (this.selectedWidgetType === 'Production') {
                if (this.selectProductionWidget === null) {
                    this.$notifyError(this.widgetNotFoundMessage);
                    return;
                }
                this.$emit(
                    'add-widget-while-editing',
                    this.selectProductionWidget
                );
            }

            // add speed widget
            if (this.selectedWidgetType === 'Speed') {
                if (this.selectSpeedWidget === null) {
                    this.$notifyError(this.widgetNotFoundMessage);
                    return;
                }
                this.$emit('add-widget-while-editing', this.selectSpeedWidget);
            }

            // add event widget
            if (this.selectedWidgetType === 'Event') {
                if (this.selectEventWidget === null) {
                    this.$notifyError(this.widgetNotFoundMessage);
                    return;
                }
                this.$emit('add-widget-while-editing', this.selectEventWidget);
            }

            this.close();
        },
        /**
         * Include widget to the editing dashboard.
         * Emit add-widget event.
         */
        selectWidget() {
            if (!this.selectedWidgetType) {
                return;
            }

            // select KPI widget
            if (this.selectedWidgetType === 'KPI') {
                if (this.selectKpiWidget === null) {
                    this.$notifyError(this.widgetNotFoundMessage);
                    return;
                }
                this.$emit('add-widget', this.selectKpiWidget);
            }

            // select production widget
            if (this.selectedWidgetType === 'Production') {
                if (this.selectProductionWidget === null) {
                    this.$notifyError(this.widgetNotFoundMessage);
                    return;
                }
                this.$emit('add-widget', this.selectProductionWidget);
            }

            // select speed widget
            if (this.selectedWidgetType === 'Speed') {
                if (this.selectSpeedWidget === null) {
                    this.$notifyError(this.widgetNotFoundMessage);
                    return;
                }
                this.$emit('add-widget', this.selectSpeedWidget);
            }

            // select event widget
            if (this.selectedWidgetType === 'Event') {
                if (this.selectEventWidget === null) {
                    this.$notifyError(this.widgetNotFoundMessage);
                    return;
                }
                this.$emit('add-widget', this.selectEventWidget);
            }

            this.close();
        },
        /**
         * Reset selected options.
         */
        resetSelectedOptions() {
            this.selectedWidgetType = null;
            this.kpi.selectedMetric = null;
            this.kpi.selectedPeriod = null;
            this.production.selectedCountType = null;
            this.production.selectedUnit = null;
            this.production.selectedPeriod = null;
            this.production.selectedShiftOption = null;
            this.production.selectedQuantityUnit = null;
            this.speed.selectedUnit = null;
            this.speed.selectedTimeUnit = null;
            this.speed.selectedPeriod = null;
            this.event.selectedMetric = null;
            this.event.selectedPeriod = null;
        },
        /**
         * Format the widget type label.
         * @param {String} type - Widget type.
         * @returns {String} - Formatted widget type label.
         */
        getWidgetTypeLabel(type) {
            return `${this.$t(type)} - ${this.$t(WIDGET_DESCRIPTIONS[type])}`;
        },
        /**
         * Sort an array of objects by value.
         * @param {Array} array - Array to sort.
         * @returns {Array} - Sorted array.
         */
        sortArrayByValue(array) {
            array.sort((a, b) => a.value.localeCompare(b.value));
        },
        /**
         * Updates the selected quantity unit based on the unit of measure.
         */
        updateSelectedQuantityUnit() {
            if (!this.unitMeasure || this.unitMeasure === 'litre') {
                this.production.selectedQuantityUnit = 'Volume';
            } else {
                this.production.selectedQuantityUnit = 'Weight';
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.select-widget-card {
    width: 100%;
    max-width: 600px !important;
}
:deep(.q-bar--standard) {
    height: auto !important;
    padding: 5px 12px !important;
}
</style>
